import { BaseComponent, BElement } from "utils/base-component"

/** Same as `--small-up` in postcss.config.js (576px). */
const SMALL_UP_MEDIA_QUERY = "(min-width: 36rem)"
const SHORT_VIEWPORT_MEDIA_QUERY = "(max-height: 1080px)"

function isBelowSmallUpViewport(): boolean {
  return !window.matchMedia(SMALL_UP_MEDIA_QUERY).matches
}

export class LocationSubheader extends BaseComponent {
  static cssClass = "location-subheader"

  private navButtons!: BElement[]
  private subNavContainers!: BElement[]
  private stickyNavWrapper!: HTMLElement | null
  private stickyNavContainer!: HTMLElement | null
  private stickyNavButton!: HTMLElement | null
  private isNavHiddenByToggle = false
  private isNavHiddenByObserver = false

  init() {
    this.stickyNavWrapper = document.querySelector(
      `.${this.elementClassName("sticky-nav-wrapper")}`,
    ) as HTMLElement | null

    this.stickyNavContainer = document.querySelector(
      `.${this.elementClassName("sticky-nav-container")}`,
    ) as HTMLElement | null

    this.stickyNavButton = document.getElementById("sticky-nav-button")

    this.navButtons = Array.from(
      document.querySelectorAll(`.${this.elementClassName("nav-button")}`),
    ).map((el) => new BElement(el, this.elementClassName("nav-button")))

    this.subNavContainers = Array.from(
      document.querySelectorAll(`.${this.elementClassName("sub-nav-container")}`),
    ).map((el) => new BElement(el, this.elementClassName("sub-nav-container")))

    // Safety check: ensure we have buttons and containers
    if (this.navButtons.length === 0 || this.subNavContainers.length === 0) {
      return
    }

    // Use server-rendered current-page state to set initial active tab.
    this.setInitialActiveTab()

    if (this.stickyNavContainer) {
      this.syncStickyNavHeight()
      window.addEventListener("resize", () => this.syncStickyNavHeight())
    }

    // Set up click handlers for nav buttons
    this.navButtons.forEach((button) => {
      button.element.addEventListener("click", () => {
        const tabId = button.element.id
        if (tabId) {
          this.switchTab(tabId)
        }
      })
    })

    // Sticky nav toggle: show/hide button when nav is in sticky position, and allow user to hide the bar
    if (this.stickyNavWrapper && this.stickyNavContainer && this.stickyNavButton) {
      type StickyNavState = {
        wasStickyActive: boolean
        hasManualToggleOverride: boolean
        prevBelowSmallUp: boolean
      }

      let ticking = false
      const stickyNavState: StickyNavState = {
        wasStickyActive: false,
        hasManualToggleOverride: false,
        prevBelowSmallUp: isBelowSmallUpViewport(),
      }
      const containerHiddenClass = `${this.elementClassName("sticky-nav-container")}--hidden`
      const buttonActiveClass = `${this.elementClassName("sticky-nav-button")}--active`
      const buttonNavHiddenClass = `${this.elementClassName("sticky-nav-button")}--nav-hidden`
      // Sidebar affiliate badges use a different sticky `top` depending on the sticky bar state
      const stickyNavShownClass = "sticky-nav-shown"
      const stickyNavHiddenClass = "sticky-nav-hidden"

      const syncAffiliateBadgesStickyTop = (isStickyActive: boolean, isNavHidden: boolean) => {
        const { classList } = document.documentElement
        classList.toggle(stickyNavShownClass, isStickyActive && !isNavHidden)
        classList.toggle(stickyNavHiddenClass, isStickyActive && isNavHidden)
      }

      const getCurrentNavHiddenState = () =>
        stickyNavState.hasManualToggleOverride
          ? this.isNavHiddenByToggle
          : this.isNavHiddenByToggle || this.isNavHiddenByObserver

      const resetToggleState = () => {
        this.isNavHiddenByToggle = false
        stickyNavState.hasManualToggleOverride = false
      }

      const applyStickyNavActiveState = (isStickyActive: boolean) => {
        this.stickyNavWrapper!.classList.toggle("active", isStickyActive)
        this.stickyNavButton!.classList.toggle(buttonActiveClass, isStickyActive)
      }

      const applyDefaultHiddenStateForViewport = (belowSmallUp: boolean) => {
        this.isNavHiddenByToggle = belowSmallUp
        stickyNavState.hasManualToggleOverride = false
      }

      const applyNavVisibility = (isStickyActive: boolean) => {
        const isNavHidden = getCurrentNavHiddenState()
        this.stickyNavButton!.classList.toggle(buttonNavHiddenClass, isNavHidden)
        this.stickyNavContainer!.classList.toggle(containerHiddenClass, isNavHidden)
        syncAffiliateBadgesStickyTop(isStickyActive, isNavHidden)
      }

      const enterStickyState = (belowSmallUp: boolean) => {
        const hasCrossedViewportBreakpoint = belowSmallUp !== stickyNavState.prevBelowSmallUp
        if (!stickyNavState.wasStickyActive || hasCrossedViewportBreakpoint) {
          applyDefaultHiddenStateForViewport(belowSmallUp)
        }
        stickyNavState.wasStickyActive = true
        applyStickyNavActiveState(true)
        applyNavVisibility(true)
      }

      const exitStickyState = () => {
        stickyNavState.wasStickyActive = false
        applyStickyNavActiveState(false)
        this.stickyNavButton!.classList.remove(buttonNavHiddenClass)
        this.stickyNavContainer!.classList.remove(containerHiddenClass)
        resetToggleState()
        syncAffiliateBadgesStickyTop(false, false)
      }

      const updateStickyState = () => {
        const { top } = this.stickyNavWrapper!.getBoundingClientRect()
        // Nav is in sticky position when its top edge has reached the viewport (top <= 8px)
        const isStickyActive = top <= 8
        const belowSmallUp = isBelowSmallUpViewport()

        if (isStickyActive) {
          enterStickyState(belowSmallUp)
        } else {
          exitStickyState()
        }

        stickyNavState.prevBelowSmallUp = belowSmallUp
        ticking = false
      }

      const onScroll = () => {
        if (!ticking) {
          ticking = true
          window.requestAnimationFrame(updateStickyState)
        }
      }

      this.stickyNavButton.addEventListener("click", () => {
        if (!this.stickyNavButton!.classList.contains(buttonActiveClass)) {
          return
        }
        const wasNavHidden = getCurrentNavHiddenState()
        stickyNavState.hasManualToggleOverride = true
        this.isNavHiddenByToggle = !wasNavHidden
        applyNavVisibility(true)
        // rAF: class/transform on the sticky container updates layout; sync after that frame.
        requestAnimationFrame(() => {
          applyNavVisibility(true)
        })
      })

      const forecastTableContent = document.querySelector(".forecast-table__content")
      if (forecastTableContent) {
        const shortViewportQuery = window.matchMedia(SHORT_VIEWPORT_MEDIA_QUERY)
        const syncObserverViewportState = () => {
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
            if (this.stickyNavWrapper!.classList.contains("active")) {
              applyNavVisibility(true)
            }
          }
        }

        const visibilityThreshold = 0.65
        const observerOptions = {
          threshold: visibilityThreshold,
          // Shrink viewport bottom so table must move further into view before hiding nav.
          rootMargin: "0px 0px -30% 0px",
        }
        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(([entry]) => {
          if (stickyNavState.hasManualToggleOverride) {
            return
          }
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
          } else {
            // Hide nav only when forecast table is mostly in view.
            this.isNavHiddenByObserver = entry.intersectionRatio >= visibilityThreshold
          }
          if (this.stickyNavWrapper!.classList.contains("active")) {
            applyNavVisibility(true)
          }
        }, observerOptions)

        shortViewportQuery.addEventListener("change", syncObserverViewportState)
        syncObserverViewportState()
        observer.observe(forecastTableContent)
      }

      updateStickyState()
      window.addEventListener("scroll", onScroll, { passive: true })
      window.addEventListener("resize", onScroll)
    }
  }

  private syncStickyNavHeight() {
    if (!this.stickyNavContainer) return
    this.stickyNavContainer.style.setProperty(
      "--sticky-nav-height",
      `${this.stickyNavContainer.offsetHeight}px`,
    )
  }

  private setInitialActiveTab() {
    const currentPageButton =
      this.navButtons.find((button) => button.element.classList.contains("current-page"))
        ?.element || null
    const initialTabId = currentPageButton?.id || this.navButtons[0]?.element.id
    if (initialTabId) {
      this.switchTab(initialTabId)
    }
  }

  /**
   * Switches to a specific tab by ID
   * @param tabId - The ID of the tab to activate
   */
  private switchTab(tabId: string) {
    // Remove active class from all buttons and containers
    this.navButtons.forEach((button) => {
      const isActive = button.element.id === tabId
      button.element.classList.toggle("active", isActive)
    })

    this.subNavContainers.forEach((container) => {
      const isActive = container.element.id === tabId
      container.element.classList.toggle("active", isActive)

      // Also toggle active on the sub-nav-scroll-container inside
      const scrollContainer = container.element.querySelector(
        `.${this.elementClassName("sub-nav-scroll-container")}`,
      )
      if (scrollContainer) {
        scrollContainer.classList.toggle("active", isActive)
      }
    })
  }
}
